import { Expose, classToPlain, plainToClass } from './ClassTransformer';
import { CreateSerializer } from './CreateSerializer';
export const ABSTRACT_SERIALIZE_PROPERTY = '_serializedType';
/**
 * Transforms instances of an abstract type into their implementations.
 *
 * This is done by serializing the implemented type along with a `_serializedType` property
 * that is used when deserializing to create an instance of the concrete class, not abstract class.
 *
 * eg:
 * abstract class Foo {}
 * class Bar extends Foo {}
 *
 * class Contract {
 *  @Serialize(() => Foo)
 *  x: Foo // `x` will be deserialized to type Bar
 * }
 *
 * const json: string = serialize(Object.assign(new Contract(), x: new Bar()))
 * const contract: Contract = deserialize(Contract, json)
 * contract.x // Will be instanceOf Bar, not Foo
 * @param abstractClass The base abstract class
 * @param implementations An array of class types that extend abstractClass
 */
export function CreateAbstractSerializer(abstractClass, implementations) {
    checkImplementationsHaveUniqueDiscriminators(implementations);
    /* eslint-disable object-shorthand */
    Object.defineProperty(abstractClass.prototype, ABSTRACT_SERIALIZE_PROPERTY, {
        get: function () {
            const implementation = implementations.find(i => i.constructor === this.constructor);
            return implementation ? implementation.discriminator : this.constructor.name;
        },
        set: function () {
            // Intentional NOOP to support Object.assign
        },
        enumerable: true,
        configurable: true,
    });
    /* eslint-enable */
    Expose()(abstractClass, ABSTRACT_SERIALIZE_PROPERTY);
    return CreateSerializer(defaultSerializer, defaultDeserializer(implementations));
}
function checkImplementationsHaveUniqueDiscriminators(implementations) {
    const implementationNames = new Map();
    implementations
        .map(implementation => implementation.discriminator)
        .forEach(name => implementationNames.has(name)
        ? implementationNames.get(name).count++
        : implementationNames.set(name, { count: 1 }));
    const implementationsWithNonUniqueDiscriminators = Array.from(implementationNames.keys())
        .map(key => ({ key, value: implementationNames.get(key) }))
        .filter(kvp => kvp.value.count > 1)
        .map(kvp => kvp.key);
    if (implementationsWithNonUniqueDiscriminators.length) {
        throw new Error('The following implementations have duplicated discriminators ' +
            '(use string values, not constructor names, for discriminators): ' +
            `${implementationsWithNonUniqueDiscriminators.join(', ')}`);
    }
}
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const defaultSerializer = (value) => {
    return classToPlain(value);
};
const defaultDeserializer = (implementations) => (value) => {
    const implementation = implementations.find(impl => impl.discriminator === value._serializedType);
    if (!implementation) {
        throw new Error(`Could not deserialize unknown class implementation of ${value._serializedType}`);
    }
    const plain = Object.assign(new implementation.constructor(), value);
    return plainToClass(implementation.constructor, plain);
};
